import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SOUND_OPTIONS, playSound } from "@/lib/sounds";
import { toast } from "sonner";
import { BookmarkPlus } from "lucide-react";

const PRESETS = [
  { id: "classic", name: "Classic", work: 25, short_break: 5, long_break: 15, interval: 4 },
  { id: "long_focus", name: "Long Focus", work: 50, short_break: 10, long_break: 30, interval: 4 },
  { id: "sprint", name: "Sprint", work: 15, short_break: 3, long_break: 10, interval: 4 },
];

const FALLBACK_DEFAULTS = {
  work_minutes: 25,
  short_break_minutes: 5,
  long_break_minutes: 15,
  long_break_interval: 4,
  sound: "bell",
};

function presetIdFor(values) {
  const match = PRESETS.find(
    (p) =>
      p.work === values.work_minutes &&
      p.short_break === values.short_break_minutes &&
      p.long_break === values.long_break_minutes &&
      p.interval === values.long_break_interval
  );
  return match ? match.id : "custom";
}

export default function CreateTimer() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [usingDefaults, setUsingDefaults] = useState(false);
  const [form, setForm] = useState({
    name: "Focus Session",
    work_minutes: 25,
    short_break_minutes: 5,
    long_break_minutes: 15,
    long_break_interval: 4,
    sound: "bell",
    shared: true,
    team_id: "",
  });
  const [presetId, setPresetId] = useState("classic");
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    api.get("/teams").then(({ data }) => setTeams(data || [])).catch(() => setTeams([]));
    api
      .get("/user/preferences")
      .then(({ data }) => {
        const prefs = { ...FALLBACK_DEFAULTS, ...(data || {}) };
        setForm((f) => ({
          ...f,
          work_minutes: prefs.work_minutes,
          short_break_minutes: prefs.short_break_minutes,
          long_break_minutes: prefs.long_break_minutes,
          long_break_interval: prefs.long_break_interval,
          sound: prefs.sound,
        }));
        setPresetId(presetIdFor(prefs));
        setUsingDefaults(true);
      })
      .catch(() => {
        // keep fallback in form
      });
  }, []);

  const applyPreset = (id) => {
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    setPresetId(id);
    setUsingDefaults(false);
    setForm((f) => ({
      ...f,
      work_minutes: p.work,
      short_break_minutes: p.short_break,
      long_break_minutes: p.long_break,
      long_break_interval: p.interval,
    }));
  };

  const setField = (k, v) => {
    setPresetId("custom");
    setUsingDefaults(false);
    setForm((f) => ({ ...f, [k]: v }));
  };

  const saveAsDefaults = async () => {
    setSavingDefaults(true);
    try {
      await api.put("/user/preferences", {
        work_minutes: form.work_minutes,
        short_break_minutes: form.short_break_minutes,
        long_break_minutes: form.long_break_minutes,
        long_break_interval: form.long_break_interval,
        sound: form.sound,
      });
      setUsingDefaults(true);
      toast.success("Saved as your defaults");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save defaults");
    } finally {
      setSavingDefaults(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, team_id: form.team_id || null };
      const { data } = await api.post("/timers", payload);
      toast.success("Timer created");
      navigate(`/timer/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create timer");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-3xl px-6 py-12">
        <div className="section-label mb-3">timer · new</div>
        <h1 className="font-display font-bold text-4xl tracking-tight">Configure a Focus Block</h1>
        <p className="mt-2 font-mono text-sm text-muted-foreground">{">"} pick a preset or dial in custom durations</p>

        <form onSubmit={submit} className="mt-10 border border-border bg-card" data-testid="create-timer-form">
          <div className="border-b border-border p-6 space-y-2">
            <Label className="font-mono text-[11px] uppercase tracking-widest" htmlFor="name">timer_name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              required
              className="rounded-none font-mono"
              placeholder="Marketing sprint, Deep work, Standup..."
              data-testid="create-timer-name-input"
            />
          </div>

          <div className="border-b border-border p-6">
            <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-3">
              <span className="text-primary">//</span> presets
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border">
              {PRESETS.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => applyPreset(p.id)}
                  className={`bg-background p-4 text-left transition-colors hover:bg-primary/10 ${
                    presetId === p.id ? "bg-primary/15 ring-1 ring-primary" : ""
                  }`}
                  data-testid={`preset-${p.id}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">// {p.id}</div>
                  <div className="font-display font-bold text-lg mt-1">{p.name}</div>
                  <div className="font-mono text-xs text-muted-foreground mt-1">{p.work}/{p.short_break}/{p.long_break}</div>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPresetId("custom")}
                className={`bg-background p-4 text-left transition-colors hover:bg-primary/10 ${
                  presetId === "custom" ? "bg-primary/15 ring-1 ring-primary" : ""
                }`}
                data-testid="preset-custom"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">// custom</div>
                <div className="font-display font-bold text-lg mt-1">Custom</div>
                <div className="font-mono text-xs text-muted-foreground mt-1">dial it in</div>
              </button>
            </div>
          </div>

          <div className="border-b border-border p-6 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { k: "work_minutes", label: "work_min", min: 1, max: 180 },
              { k: "short_break_minutes", label: "short_break", min: 1, max: 60 },
              { k: "long_break_minutes", label: "long_break", min: 1, max: 120 },
              { k: "long_break_interval", label: "long_every", min: 2, max: 12 },
            ].map(({ k, label, min, max }) => (
              <div key={k} className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-widest" htmlFor={k}>{label}</Label>
                <Input
                  id={k}
                  type="number"
                  min={min}
                  max={max}
                  value={form[k]}
                  onChange={(e) => setField(k, Math.max(min, Math.min(max, parseInt(e.target.value || "0", 10))))}
                  className="rounded-none font-mono"
                  data-testid={`input-${k}`}
                />
              </div>
            ))}
          </div>

          <div className="border-b border-border p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-widest">completion_sound</Label>
              <div className="flex items-center gap-2">
                <Select value={form.sound} onValueChange={(v) => { setUsingDefaults(false); setForm((f) => ({ ...f, sound: v })); }}>
                  <SelectTrigger className="rounded-none font-mono flex-1" data-testid="sound-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {SOUND_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} className="font-mono" data-testid={`sound-option-${o.value}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-none border-border font-mono uppercase tracking-widest"
                  onClick={() => playSound(form.sound)}
                  data-testid="sound-preview-btn"
                >
                  Preview
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between border border-border p-4">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest">shared_room</div>
                <div className="font-mono text-xs text-muted-foreground mt-1">{">"} let teammates join via a link</div>
              </div>
              <Switch
                checked={form.shared}
                onCheckedChange={(v) => setForm((f) => ({ ...f, shared: v }))}
                data-testid="shared-toggle"
              />
            </div>
          </div>

          <div className="border-b border-border p-6 space-y-2">
            <Label className="font-mono text-[11px] uppercase tracking-widest">team_scope</Label>
            <Select
              value={form.team_id || "__none__"}
              onValueChange={(v) => setForm((f) => ({ ...f, team_id: v === "__none__" ? "" : v }))}
            >
              <SelectTrigger className="rounded-none font-mono" data-testid="team-select">
                <SelectValue placeholder="No team (personal)" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="__none__" className="font-mono" data-testid="team-option-none">
                  // none — personal only
                </SelectItem>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="font-mono" data-testid={`team-option-${t.id}`}>
                    {t.name} · {t.member_count} member{t.member_count === 1 ? "" : "s"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="font-mono text-xs text-muted-foreground">{">"} optional — team members can view this timer from their Teams page</div>
          </div>

          <div className="p-6 flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-none border-border font-mono uppercase tracking-widest"
              onClick={saveAsDefaults}
              disabled={savingDefaults || usingDefaults}
              data-testid="save-as-defaults-btn"
              title={usingDefaults ? "Already saved as your defaults" : "Use these values to pre-fill future timers"}
            >
              <BookmarkPlus className="mr-2 h-3.5 w-3.5" />
              {savingDefaults ? "Saving…" : usingDefaults ? "Saved as defaults" : "Save as my defaults"}
            </Button>
            <div className="flex items-center gap-3 ml-auto">
              <Button type="button" variant="outline" className="rounded-none border-border font-mono uppercase tracking-widest" onClick={() => navigate("/dashboard")} data-testid="create-timer-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={busy} className="rounded-none font-mono uppercase tracking-widest" data-testid="create-timer-submit">
                {busy ? "Creating…" : "Create Timer"}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
